import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

// SPA scroll restoration. React Router 6's <BrowserRouter> doesn't restore
// scroll on its own (only the data router's <ScrollRestoration> does), so
// every back/forward landing scrolls to the top — annoying when you click
// into an account from row 87 of a long list and want to return to it.
//
// Behavior:
//   - Forward navigation (PUSH/REPLACE): scroll to top of the new page
//   - Back/forward navigation (POP): restore the scroll position the user
//     had on that page before they left
//
// Positions are keyed by location.key (a unique id React Router assigns to
// every history entry) so two visits to the same URL via separate clicks
// remember their own scroll positions independently.
//
// Positions live in-memory (Map). Full page reload starts fresh — fine
// because reload also generates new keys.
export default function ScrollRestoration() {
  const location = useLocation();
  const navType = useNavigationType();
  const positionsRef = useRef(new Map());

  // Take manual control away from the browser's default scroll-restoration
  // (which can fight with our restoration on POP).
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      const prev = window.history.scrollRestoration;
      window.history.scrollRestoration = 'manual';
      return () => { window.history.scrollRestoration = prev; };
    }
  }, []);

  // Continuously save scroll for the current location key. Passive listener
  // so we don't interfere with native scrolling perf.
  useEffect(() => {
    const key = location.key;
    const onScroll = () => {
      positionsRef.current.set(key, window.scrollY);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    // Capture initial position too (covers the case where the page never
    // scrolls before the user navigates away).
    positionsRef.current.set(key, window.scrollY);
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.key]);

  // On location change, scroll appropriately. useLayoutEffect to avoid the
  // user briefly seeing the old scroll position before the jump.
  useLayoutEffect(() => {
    if (navType === 'POP') {
      const saved = positionsRef.current.get(location.key) ?? 0;
      window.scrollTo(0, saved);
    } else {
      window.scrollTo(0, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  return null;
}
